import { useEffect } from 'react';

import * as domainLogosSelectors from 'app-components/flux/domain-logos/domainLogosSelectors';
import { fetchLogoById, fetchLogosByUrl } from 'app-components/flux/domain-logos/domainLogosSlice';
import type { DomainLogoProps } from 'app-components/flux/domain-logos/domainLogosTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';
import type { AppDispatch } from 'app-components/utils/redux-store';

import { payeeNameToDomain } from './payeeToDomain';

type LogoFields = {
  logoId?: string | null;
  logoUrl?: string | null;
  payee?: string | null;
};

export type ResolvedDomainLogoFields = Pick<LogoFields, 'logoId' | 'logoUrl'> & {
  /** Optional lower-priority domain lookup, such as a domain derived from a payee name. */
  fallbackLogoUrl?: string | null;
};

export type UseResolvedDomainLogoOptions = {
  abort?: boolean;
};

type LogoRequestKey = `id:${string}` | `url:${string}`;

// This cache contains only active requests. Settled requests are removed so a
// remount or a Redux-store reset can request the same logo again when needed.
const inFlightLogoRequestsByDispatch = new WeakMap<AppDispatch, Map<LogoRequestKey, Promise<unknown>>>();

const getInFlightLogoRequests = (dispatch: AppDispatch): Map<LogoRequestKey, Promise<unknown>> => {
  const existingRequests = inFlightLogoRequestsByDispatch.get(dispatch);
  if (existingRequests) return existingRequests;

  const requests = new Map<LogoRequestKey, Promise<unknown>>();
  inFlightLogoRequestsByDispatch.set(dispatch, requests);
  return requests;
};

const clearInFlightLogoRequest = (
  requests: Map<LogoRequestKey, Promise<unknown>>,
  requestKey: LogoRequestKey,
  request: Promise<unknown>,
): void => {
  if (requests.get(requestKey) === request) {
    requests.delete(requestKey);
  }
};

/**
 * Resolves and, when necessary, fetches a domain logo for fields whose source
 * precedence has already been decided by the caller.
 */
export const useResolvedDomainLogo = (
  fields?: ResolvedDomainLogoFields | null,
  options: UseResolvedDomainLogoOptions = {},
): DomainLogoProps | null => {
  const dispatch = useAppDispatch();
  const { abort = false } = options;
  const logoId = fields?.logoId || null;
  const logoUrl = fields?.logoUrl || null;
  const fallbackLogoUrl = fields?.fallbackLogoUrl || null;

  const urlLogo = useAppSelector((state) => (logoUrl ? domainLogosSelectors.selectLogoByUrl(state, logoUrl) : null));
  const idLogo = useAppSelector((state) =>
    !urlLogo && logoId ? domainLogosSelectors.selectLogoById(state, logoId) : null,
  );
  const fallbackLogo = useAppSelector((state) =>
    !urlLogo && !idLogo && fallbackLogoUrl ? domainLogosSelectors.selectLogoByUrl(state, fallbackLogoUrl) : null,
  );
  const logo = urlLogo || idLogo || fallbackLogo;
  // A domain the service already resolved without a logo stays null for the
  // session; only rejected requests are retried on remount.
  const requestUrl = logoUrl || fallbackLogoUrl;
  const isResolvedMiss = useAppSelector((state) =>
    !logoId && requestUrl ? domainLogosSelectors.selectIsResolvedLogoMiss(state, requestUrl) : false,
  );

  useEffect(() => {
    if (abort || logo || isResolvedMiss) return;
    if (!logoId && !requestUrl) return;

    const requestKey: LogoRequestKey = logoId ? `id:${logoId}` : `url:${requestUrl}`;
    const inFlightLogoRequests = getInFlightLogoRequests(dispatch);
    if (inFlightLogoRequests.has(requestKey)) return;

    let request: Promise<unknown>;
    if (logoId) {
      request = dispatch(fetchLogoById(logoId));
    } else if (requestUrl) {
      request = dispatch(fetchLogosByUrl([requestUrl]));
    } else {
      return;
    }
    inFlightLogoRequests.set(requestKey, request);
    void request.then(
      () => clearInFlightLogoRequest(inFlightLogoRequests, requestKey, request),
      () => clearInFlightLogoRequest(inFlightLogoRequests, requestKey, request),
    );
  }, [abort, dispatch, isResolvedMiss, logo, logoId, requestUrl]);

  return abort ? null : logo || null;
};

export const useLogoForTxnOrModel = (
  txn?: LogoFields | null,
  model?: { transaction?: LogoFields | null },
  usePayee?: boolean,
  abort?: boolean,
): DomainLogoProps | null => {
  const featureOn = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'logosSeriesRelease'));
  const allowPayeeAbstraction = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'logosPayeeAutoLookupRelease'),
  );

  const logoId = model?.transaction?.logoId || txn?.logoId || null;
  const logoUrl = model?.transaction?.logoUrl || txn?.logoUrl || null;

  let payeeUrl: string | null = null;
  if (allowPayeeAbstraction && !abort && !logoUrl && usePayee) {
    // if no logo already provided, fallback to payee URL abstraction if allowed
    const resolvedPayee = model?.transaction?.payee || txn?.payee;
    if (resolvedPayee) {
      payeeUrl = payeeNameToDomain(resolvedPayee);
    }
  }

  const displayLogo = useResolvedDomainLogo(
    {
      logoId,
      logoUrl,
      fallbackLogoUrl: payeeUrl,
    },
    { abort },
  );

  if (abort || !featureOn) return null;

  return displayLogo;
};
